// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getDataLayer } from "@sc/App/GoogleAnalyticsConnector/Utilities/DataLayer";
import { xGoogleAnalyticsState } from "@sc/App/GoogleAnalyticsConnector/Store/Reducers";
import { GOOGLE_EVENTS } from "@sc/XDL/constants";
import { parseProductCategories } from "@sc/XDL/Utilities";
import { ApiHandler, createHandlerChainRunner } from "@insite/client-framework/HandlerCreator";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";

type HandlerType = ApiHandler<{
    includeCheckoutEvent: boolean | undefined;
    checkoutEventType: string;
}>;

const XngGaCheckoutStepsEvent: HandlerType = props => {
    const state = props.getState() as xGoogleAnalyticsState;
    const cart = getCurrentCartState(props.getState()).value;
    const session = getSession(state);
    const locationId = session?.pickUpWarehouse?.id;

    const { includeCheckoutEvent, checkoutEventType } = props.parameter;

    if (includeCheckoutEvent) {
        const dataLayer = getDataLayer();

        if (!dataLayer) {
            return;
        }

        dataLayer.push({ ecommerce: null });

        const googleAnalyticsData = {
            event: checkoutEventType,
            ecommerce: {
                ...(checkoutEventType === GOOGLE_EVENTS.ADD_SHIPPING_INFO && {
                    shipping_tier: cart?.carrier?.description || "",
                }),
                ...(checkoutEventType === GOOGLE_EVENTS.ADD_PAYMENT_INFO && {
                    payment_type: cart?.paymentMethod?.description || "",
                }),
                currency: state.context.session.currency?.currencyCode,
                value: cart?.orderSubTotal,
                affiliation: state.xGoogleAnalytics.affiliation,
                sales_rep: state.xGoogleAnalytics.salesRep || "",
                is_rep: state.xGoogleAnalytics.isRep || "",
                bill_to_num: state.xGoogleAnalytics.billToNum || "",
                ship_to_num: state.xGoogleAnalytics.shipToNum || "",
                cust_type: state.xGoogleAnalytics.customerType || "",
                login_type: state.xGoogleAnalytics.loginType || "",
                warehouse: state.xGoogleAnalytics.warehouse || "",

                items: cart?.cartLines?.map((cartLine, idx) => ({
                    item_id: cartLine.erpNumber,
                    item_name: cartLine.shortDescription,
                    affiliation: state.xGoogleAnalytics.affiliation,
                    discount: cartLine.pricing
                        ? cartLine.pricing.unitNetPrice < cartLine.pricing.unitListPrice
                            ? cartLine.pricing.unitListPrice - cartLine.pricing.unitNetPrice
                            : 0
                        : 0,
                    index: idx,
                    item_brand: cartLine.brand?.name,
                    location_id: locationId,
                    price: cartLine.pricing?.unitNetPrice,
                    quantity: cartLine.qtyOrdered,
                    ...parseProductCategories(
                        cartLine?.properties?.itemCategoriesShortDescription,
                        true,
                        state.xGoogleAnalytics.includeProductCategory,
                    ),
                })),
            },
        };

        dataLayer.push({ ecommerce: null });
        dataLayer.push(googleAnalyticsData);
    }
};

export const chain = [XngGaCheckoutStepsEvent];

const xngGaCheckoutStepsEvent = createHandlerChainRunner(chain, "XngGaCheckoutStepsEvent");
export default xngGaCheckoutStepsEvent;
